// src/components/OrderEntry/api/orderEntryApi.js
import axios, { axiosWithoutAuth } from '../../../axiosClient.js';

// ============================================================================
// BLOCK OEA.0 — Order Entry API client
// Purpose:
//  - Single place for frontend -> backend HTTP calls used by OrderEntry.jsx
// Notes:
//  - All functions return normalized shapes to keep React effects simple.
// ============================================================================

// ============================================================================
// BLOCK OEA.0H — Small normalization helpers (keep API wrappers DRY)
// ============================================================================
const asArray = (v) => (Array.isArray(v) ? v : []);
const asObject = (v) => (v && typeof v === 'object' ? v : null);
const trimStr = (v) => (typeof v === 'string' ? v.trim() : String(v ?? '').trim());

// ============================================================================
// BLOCK OEA.0Q — Encode literal query values before the shared HTTP client
// ============================================================================
// Keep raw text in callers and encode exactly once at the URL boundary.
// Building the query here avoids relying on shared params serialization:
// P&OPE becomes P%26OPE, and the server receives the original P&OPE value.
// All GET parameters in this module are scalar values. Omit null/undefined
// optional values, while preserving empty strings, zero, and false.
function getOrderEntry(url, { params = {} } = {}) {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      query.append(key, String(value));
    }
  });

  const queryString = query.toString();
  return axios.get(queryString ? `${url}?${queryString}` : url);
}

function normalizeSalespersonRow(row) {
  const r = asObject(row);
  if (!r) {
    return null;
  }
  return {
    div: trimStr(r.div),
    no: trimStr(r.no),
    name: trimStr(r.name),
    email: trimStr(r.email),
  };
}

// ============================================================================
// BLOCK OEA.1 — Order Entry endpoints (/api/order-entry/*)
// ============================================================================

// OEA.1.1 — ShipVia codes (carrier + FC + international)
export async function getShipViaCodes({
  carrier,
  fc,
  international, // optional override
  shipFromCountry,
  shipToCountry,
}) {
  const params = {
    carrier,
    fc,
  };

  // Optional override (still supported)
  if (international != null) {
    params.international = international ? 1 : 0;
  }

  // NEW: country-based derivation inputs
  if (shipFromCountry) {
    params.shipFromCountry = shipFromCountry;
  }
  if (shipToCountry) {
    params.shipToCountry = shipToCountry;
  }

  const { data } = await getOrderEntry('/api/order-entry/shipvia-codes', { params });
  return asArray(data?.data);
}

// OEA.1.2 — ZIP → Customers
export async function getCustomersByZip({ company, zip }) {
  const { data } = await getOrderEntry('/api/order-entry/customers-by-zip', {
    params: { company, zip },
  });
  return asArray(data?.data);
}

// OEA.1.2A — Ship-to addresses by company (one-time page load lookup)
export async function getShipToAddresses({ company, search }) {
  const { data } = await getOrderEntry('/api/order-entry/ship-to-addresses', {
    params: { company, search },
  });
  return asArray(data?.data);
}

// OEA.1.3 — Customer → Addresses
export async function getAddressesByCustomer({ company, zip, customerNo }) {
  const { data } = await getOrderEntry('/api/order-entry/addresses-by-customer', {
    params: { company, zip, customerNo },
  });
  return asArray(data?.data);
}

// OEA.1.4 — Customer Notes
export async function getCustomerNotes({ company, customerNo, shipToCode }) {
  const { data } = await getOrderEntry('/api/order-entry/customer-notes', {
    params: { company, customerNo, shipToCode: shipToCode || '' },
  });
  // Return full payload { notes, flags } when available so callers can use explicit flags
  return data || { notes: [], flags: {} };
}

// OEA.1.5 — House flags (split/house)
export async function getHouseFlags({ customerNo, shipToCode }) {
  const { data } = await getOrderEntry('/api/order-entry/house-flags', {
    params: { customerNo, shipToCode },
  });
  return {
    splitCommission: Boolean(data?.splitCommission),
    houseAccount: Boolean(data?.houseAccount),
  };
}

// OEA.1.5A — ConfirmTo names (last 365d) for MASID + ShipTo
// Returns: string[] (trimmed)
export async function getConfirmToNamesLast365({ company, customerNo, shipToCode, days = 365 }) {
  const { data } = await getOrderEntry('/api/order-entry/confirm-to-names', {
    params: {
      ...(company ? { company } : {}),
      customerNo,
      shipToCode: shipToCode || '',
      days,
    },
  });

  const pickArray = (...candidates) => {
    for (const c of candidates) {
      if (Array.isArray(c)) return c;
    }
    return [];
  };

  const a = pickArray(data?.data, data?.rows, data?.data?.rows, data);

  return a.map((v) => trimStr(v)).filter(Boolean);
}

// OEA.1.6 — Most recent FOB (freight account inference)
export async function getMostRecentFob({ company, customerNo, shipToCode, carrier }) {
  const params = { company, customerNo, shipToCode };
  if (carrier) {
    params.carrier = carrier;
  }

  const { data } = await getOrderEntry('/api/order-entry/most-recent-fob', { params });
  return typeof data?.fob === 'string' ? data.fob.trim() : '';
}

// OEA.1.6A — List saved freight accounts (CustShipAcctNo)
export async function listSavedFreightAccounts({
  company = 'tzi',
  customerNo,
  carrier,
  includeShipTos = false,
}) {
  const { data } = await getOrderEntry('/api/order-entry/cust-ship-acct/list', {
    params: { company, customerNo, carrier, includeShipTos: includeShipTos ? '1' : '0' },
  });

  if (!Array.isArray(data?.rows) || (includeShipTos && !Array.isArray(data?.shipTos))) {
    throw new Error('Invalid freight account response. Update the server and client together.');
  }
  return data;
}

// OEA.1.6B — Upsert freight account (ship-to or customer-wide)
export async function saveFreightAccount({ customerNo, shipToCode = '', carrier, accountNo }) {
  const payload = {
    customerNo,
    shipToCode: shipToCode || '',
    carrier,
    accountNo,
  };

  const { data } = await axios.post('/api/order-entry/cust-ship-acct', payload);
  return data && typeof data === 'object' ? data : { ok: true, data: payload };
}

// =============================================================================
// BLOCK OEA.1.6C — Bulk replace freight account for a MASID + carrier
// -----------------------------------------------------------------------------
// Purpose:
//   Implements the dialog option: "Replace all for this MASID".
//
// Queries all Sage ship-tos for company/customer at commit and creates missing
// CustShipAcctNo rows, then updates all saved rows for customer/carrier atomically.
// An existing blank/default is updated; a missing default is not created.
// =============================================================================
export async function bulkSetFreightAccounts({
  company = 'tzi',
  customerNo,
  carrier,
  accountNo,
  shipToCode = '',
}) {
  const payload = { company, customerNo, carrier, accountNo, shipToCode };
  const { data } = await axios.post('/api/order-entry/cust-ship-acct/bulk-set', payload);
  return data && typeof data === 'object' ? data : { ok: true, data: payload };
}

// OEA.1.6C.1 — Clear saved freight accounts (ship-to override OR all rows)
// Body:
//  - customerNo (required)
//  - carrier (required)
//  - scope: 'shipto' | 'all' (required)
//  - shipToCode (required when scope='shipto')
// Returns: { ok:true, data:{ scope, updatedCount } }
export async function clearFreightAccounts({ customerNo, carrier, scope, shipToCode = '' }) {
  const payload = {
    customerNo,
    carrier,
    scope,
    shipToCode: shipToCode || '',
  };

  const { data } = await axios.post('/api/order-entry/cust-ship-acct/clear', payload);
  return data && typeof data === 'object' ? data : { ok: true, data: payload };
}

// OEA.1.7 — Part list (autocomplete)
export async function getPartList({ company }) {
  const { data } = await getOrderEntry('/api/order-entry/part-list', { params: { company } });
  return asArray(data?.data);
}

// OEA.1.7A — Bitrhythm studies for TMS direct-ship patient popup
export async function getBitrhythmStudies({ company = 'tms', facilityZipCode }) {
  const { data } = await getOrderEntry('/api/order-entry/bitrhythm-studies', {
    params: { company, facilityZipCode },
  });
  return asArray(data?.data);
}

// OEA.1.8 — Part info (contracts + last order + special order flags)
export async function getPartInfo({ company, customerNo, shipToCode, item }) {
  const { data } = await getOrderEntry('/api/order-entry/part-info', {
    params: { company, customerNo, shipToCode, item },
  });
  return data || {};
}

// OEA.1.8A — TZI Sugar product quote lookup (runs only on committed Part No)
export async function getTziProductQuote({ customerNo, shipToCode, partNo }) {
  const { data } = await getOrderEntry('/api/order-entry/tzi-product-quote', {
    params: {
      company: 'tzi',
      customerNo,
      shipToCode: shipToCode || '',
      partNo,
    },
  });

  return {
    ok: Boolean(data?.ok),
    found: Boolean(data?.found),
    accountId: trimStr(data?.accountId),
    dateEntered: data?.dateEntered || null,
    name: trimStr(data?.name),
    mftPartNum: trimStr(data?.mftPartNum),
    discountPrice: data?.discountPrice ?? null,
    quantity: data?.quantity ?? null,
    outOfDate: Boolean(data?.outOfDate),
  };
}

// OEA.1.9 — PO duplicate check
export async function checkPoDuplicate({ company, customerNo, poNumber }) {
  const { data } = await getOrderEntry('/api/order-entry/check-po-duplicate', {
    params: { company, customerNo, poNumber },
  });
  return asArray(data?.matches);
}

// OEA.1.9A — PO format similarity check (last 3 POs for MASID + ShipTo)
export async function checkPoFormatSimilarity({ company, customerNo, shipToCode, poNumber }) {
  const { data } = await getOrderEntry('/api/order-entry/check-po-format', {
    params: { company, customerNo, shipToCode, poNumber },
  });

  return {
    score: Number(data?.score ?? 100),
    historyPoNumbers: asArray(data?.historyPoNumbers),
    enteredPoFormat: trimStr(data?.enteredPoFormat),
    historyFormats: asArray(data?.historyFormats),
  };
}

// OEA.1.10 — Sales Reps (Badge 6)
// Backend returns: { ok:true, data:{ rep:[], mngr:[], tsr:[] } }
export async function getSalesReps({ company, state, zip }) {
  const { data } = await getOrderEntry('/api/order-entry/sales-reps', {
    params: { company, state, zip },
  });

  return data && typeof data === 'object' && data.data && typeof data.data === 'object'
    ? data.data
    : data || {};
}

// OEA.1.11 — Terms dictionary
export async function getTerms() {
  const { data } = await getOrderEntry('/api/order-entry/terms');
  return asArray(data?.data);
}

// OEA.1.12 — Sugar account lookup (CustomerContracts.id_c)
// NOTE: Canonical implementation is in BLOCK OEA.SUGAR.0 below.
// (This stub block remains only to preserve historical block numbering.)

// OEA.1.13 — Special-order external notes for a set of parts
export async function getSpecialOrderNotes({ partNos }) {
  const { data } = await axios.post('/api/order-entry/special-order-notes', {
    partNos: asArray(partNos),
  });

  // Expect: { ok:true, notesByPart:{ TZ123:"External note...", ... } }
  const notesByPart =
    data?.notesByPart && typeof data.notesByPart === 'object' ? data.notesByPart : {};
  return notesByPart;
}

// ============================================================================
// BLOCK OEA.1.14 — Build legacy rows (backend only)
// ----------------------------------------------------------------------------
// Calls your Fastify backend:
//   POST /api/order-entry/submit-order
// Returns: { ok:true, legacyRows:[...] }
// ============================================================================
export async function buildLegacyRowsForInsert(payload) {
  const { data } = await axios.post('/api/order-entry/submit-order', payload);

  // Normalize shape defensively
  const legacyRows = asArray(data?.legacyRows);
  return {
    ok: Boolean(data?.ok),
    legacyRows,
    raw: data,
  };
}

// ============================================================================
// BLOCK OEA.1.15 — Submit legacy insert (browser → LAN)
// ----------------------------------------------------------------------------
// CRITICAL REQUIREMENT:
// - Must hit the LAN host (ip:port/insert), NOT current origin (/api/...)
// - URL must be available to the browser at runtime (preferred) or build-time
// Resolution order:
//   1) window.globalConfig.orderEntry.legacyInsertUrl  (runtime, no rebuild)
//   2) import.meta.env.VITE_ORDER_ENTRY_LEGACY_INSERT_URL (Vite)
//   3) process.env.REACT_APP_ORDER_ENTRY_LEGACY_INSERT_URL (Webpack)
// ============================================================================
function getLegacyInsertUrl() {
  // --------------------------------------------------------------------------
  // BLOCK OEA.1.15.RUNTIME.0 — Runtime config (preferred)
  // --------------------------------------------------------------------------
  const runtimeUrl =
    window?.globalConfig?.orderEntry?.legacyInsertUrl &&
    typeof window.globalConfig.orderEntry.legacyInsertUrl === 'string'
      ? window.globalConfig.orderEntry.legacyInsertUrl.trim()
      : '';

  if (runtimeUrl) {
    return runtimeUrl;
  }

  // --------------------------------------------------------------------------
  // BLOCK OEA.1.15.BUILD.VITE.0 — Vite build-time (not used here, safe fallback)
  // --------------------------------------------------------------------------

  const viteUrl =
    typeof import.meta !== 'undefined' &&
    import.meta.env &&
    typeof import.meta.env.VITE_ORDER_ENTRY_LEGACY_INSERT_URL === 'string'
      ? import.meta.env.VITE_ORDER_ENTRY_LEGACY_INSERT_URL.trim()
      : '';

  if (viteUrl) {
    return viteUrl;
  }

  // --------------------------------------------------------------------------
  // BLOCK OEA.1.15.BUILD.WEBPACK.0 — Webpack build-time
  // --------------------------------------------------------------------------

  const webpackUrl =
    typeof process !== 'undefined' &&
    process.env &&
    typeof process.env.REACT_APP_ORDER_ENTRY_LEGACY_INSERT_URL === 'string'
      ? process.env.REACT_APP_ORDER_ENTRY_LEGACY_INSERT_URL.trim()
      : '';

  return webpackUrl;
}

export async function submitLegacyInsertLocal(legacyRows) {
  const legacyInsertUrl = getLegacyInsertUrl();

  if (!legacyInsertUrl) {
    const err = new Error(
      'Missing legacy insert URL. Set window.__ORDER_ENTRY_CONFIG__.legacyInsertUrl ' +
        'or VITE_ORDER_ENTRY_LEGACY_INSERT_URL or REACT_APP_ORDER_ENTRY_LEGACY_INSERT_URL.'
    );
    err.code = 'MISSING_LEGACY_INSERT_URL';
    throw err;
  }

  // Ensure it ends with "/insert" if you want strictness (optional).
  // If you ALWAYS store the full "/insert" URL in env/config, delete this.
  const url = legacyInsertUrl.endsWith('/insert') ? legacyInsertUrl : `${legacyInsertUrl}/insert`;

  // --------------------------------------------------------------------------
  // BLOCK OEA.1.15.POST.0 — Browser → LAN insert (NO auth headers)
  // --------------------------------------------------------------------------
  // IMPORTANT:
  // - Do NOT use axiosClient here, because it auto-attaches Auth0 Bearer tokens.
  // - Legacy insert is a LAN endpoint and typically expects anonymous JSON POST.
  try {
    const res = await axiosWithoutAuth.post(url, legacyRows);
    return res.data;
  } catch (err) {
    const newErr = new Error(`Legacy insert failed (${err.message})`);
    if (err.response) {
      newErr.status = err.response.status;
      newErr.detail = err.response.data;
    } else {
      newErr.code = err.code;
    }
    throw newErr;
  }
}

// ============================================================================
// BLOCK OEA.1.16 — House Account Reps (Rep1 / Rep2)
// ============================================================================
// Returns:
//  { rep1, rep2, rep1Name, rep2Name }
// ============================================================================
export async function getHouseAccountReps({ company, customerNo, shipToCode }) {
  const { data } = await getOrderEntry('/api/order-entry/house-account-reps', {
    params: {
      ...(company ? { company } : {}),
      customerNo,
      shipToCode: shipToCode || '',
    },
  });

  return {
    mode: String(data?.mode ?? '').trim(),
    split: data?.split ?? data?.Split ?? null,
    revReqChk: data?.revReqChk ?? data?.rev_req_chk ?? 0,
    rep1: String(data?.rep1 ?? '').trim(),
    rep2: String(data?.rep2 ?? '').trim(),
    rep1Name: String(data?.rep1Name ?? '').trim(),
    rep2Name: String(data?.rep2Name ?? '').trim(),
  };
}

// ============================================================================
// BLOCK OEA.1.17 — Submit picking sheet (single-call API for React)
// ----------------------------------------------------------------------------
// This is the "one place" function:
//  1) backend builds legacyRows
//  2) frontend builds numbered payload
//  3) browser posts to LAN /insert
// Throws on any failure so OrderEntry.jsx can show one error path.
// ============================================================================
export async function submitPickingSheet(payload) {
  const built = await buildLegacyRowsForInsert(payload);

  if (!built.ok || !Array.isArray(built.legacyRows) || built.legacyRows.length === 0) {
    const err = new Error('Backend did not return legacyRows.');
    err.code = 'NO_LEGACY_ROWS';
    err.detail = built.raw;
    throw err;
  }

  // Browser → LAN insert
  return submitLegacyInsertLocal(built.legacyRows);
}

// ============================================================================
// BLOCK OEA.2 — Shipping endpoints (/api/shipping/*)
// ============================================================================

// OEA.2.1 — Single ETA call
export async function fetchShipEta(payload) {
  const { data } = await axios.post('/api/shipping/eta', payload);
  return data || {};
}

// OEA.2.2 — Batch ETA wrapper (normalizes etaMap shape)
export async function fetchShipEtaBatch(payload) {
  const data = await fetchShipEta(payload);
  const etaMap = data?.etaMap && typeof data.etaMap === 'object' ? data.etaMap : data;
  return etaMap && typeof etaMap === 'object' ? etaMap : {};
}

export async function getSalespersonEmailByName({ company, name }) {
  const { data } = await getOrderEntry('/api/order-entry/salesperson-email-by-name', {
    params: { company, name },
  });

  return normalizeSalespersonRow(data?.data);
}

// ============================================================================
// BLOCK OEA.2.20 — Salesperson email lookup
// ----------------------------------------------------------------------------
// GET /api/order-entry/salesperson-email?company=tzi&div=01&no=1234
// Returns: { div, no, name, email } or null
// ============================================================================
export async function getSalespersonEmail({ company, div, no }) {
  const { data } = await getOrderEntry('/api/order-entry/salesperson-email', {
    params: { company, div, no },
  });

  return normalizeSalespersonRow(data?.data);
}

// ============================================================================
// BLOCK OEA.SUGAR.0 — Sugar account lookup (CustomerContracts.id_c)
// ----------------------------------------------------------------------------
// Fix:
// - Remove duplicate function exports (was causing import/runtime ambiguity)
// - Provide ONE canonical getSugarAccountId() with normalized return shape
// Notes:
// - `company` is accepted for forward compatibility, but backend may ignore it.
// - Returns: { ok:boolean, id_c:string, warning:string }
// ============================================================================
export async function getSugarAccountId({ company, customerNo, shipToCode }) {
  const { data } = await getOrderEntry('/api/order-entry/sugar-account', {
    params: {
      ...(company ? { company } : {}),
      customerNo,
      shipToCode,
    },
  });

  // Support both possible backend shapes:
  // A) { ok, id_c, warning }
  // B) { ok, data:{ id_c }, warning }
  const directId = typeof data?.id_c === 'string' ? data.id_c : '';
  const nestedId = typeof data?.data?.id_c === 'string' ? data.data.id_c : '';
  const idC = directId || nestedId || '';

  return {
    ok: Boolean(data?.ok ?? Boolean(idC)),
    // keep backend/Excel parity field name without tripping camelcase
    id_c: idC,
    warning: typeof data?.warning === 'string' ? data.warning : '',
  };
}
